import { useState, useEffect, useCallback, useMemo } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AddressCheckPanel } from "@/components/portal/AddressCheckPanel";
import { Panel, PageHeader } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, CheckCircle2, Loader2, X, AlertTriangle, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MartPreview } from "@/components/portal/MartPreview";
import { ImportGuide } from "@/components/portal/ImportGuide";
import { ImportShiftWarning } from "@/components/portal/ImportShiftWarning";
import { ImportCodeSummary } from "@/components/portal/ImportCodeSummary";
import { downloadSampleMart } from "@/lib/sampleMart";

const GUIDE_KEY = "rs_import_guide_dismissed";
import { useNavigate } from "react-router-dom";
import { dayKey, fmtDayKey, fmtDateTime, localToIso, fmtZoned } from "@/lib/time";
import Papa from "papaparse";
import { checkImportDays, dropRows } from "@/lib/importCheck";

// Pin zone-less pickup times to the operator's time zone before sending
const pinPickupTimes = (csvText, column) => {
  const out = Papa.parse(csvText, { header: true, skipEmptyLines: true });
  if (!out.meta.fields?.includes(column)) return csvText;
  out.data.forEach((row) => { const iso = localToIso(row[column]); if (iso) row[column] = iso.slice(0, 16).replace("T", " "); });
  return Papa.unparse(out.data, { columns: out.meta.fields });
};

const darkField = "w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

// Fleet only stores 9 fields from a regular CSV, so fold code/mobility columns into the trip type.
const CODE_COLS = ["codes", "code", "tripcodes", "tripcode", "mobility", "mobilitycodes", "mobilitynotes", "specialneeds", "needs", "servicecodes", "spacetype", "los", "levelofservice", "vehicletype", "restrictions"];
const codeColumns = (fields, mapping) => (fields || []).filter((f) => CODE_COLS.includes(String(f).toLowerCase().replace(/[^a-z0-9]/g, "")) && !Object.values(mapping).includes(f));
const mergeCodes = (csvText, mapping) => {
  const out = Papa.parse(csvText, { header: true, skipEmptyLines: true });
  const cols = codeColumns(out.meta.fields, mapping);
  if (!cols.length) return { csvText, mapping };
  const typeCol = mapping.trip_type || "trip_type_with_codes";
  const fields = out.meta.fields.includes(typeCol) ? out.meta.fields : [...out.meta.fields, typeCol];
  out.data.forEach((row) => { row[typeCol] = [row[typeCol], ...cols.map((c) => row[c])].map((v) => String(v || "").trim()).filter(Boolean).join(" "); });
  return { csvText: Papa.unparse(out.data, { columns: fields }), mapping: { ...mapping, trip_type: typeCol } };
};
const SKIP = "__skip__";
const TARGET_FIELDS = ["external_trip_id", "scheduled_pickup", "pickup_address", "dropoff_address", "rider_name", "broker", "trip_type", "assigned_driver_name", "price"];

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const ALIASES = {
  external_trip_id: ["externaltripid", "tripid", "confirmation", "confirmationnumber", "reservationid", "legid", "trip"],
  scheduled_pickup: ["scheduledpickup", "appttime", "appointmenttime", "pickuptime", "requestedpickup", "pickupdatetime", "pickupdate", "schedtime"],
  pickup_address: ["pickupaddress", "pickup", "origin", "fromaddress", "pickuplocation", "pu", "pickupaddr"],
  dropoff_address: ["dropoffaddress", "dropoff", "destination", "toaddress", "dropofflocation", "drop", "do", "dropoffaddr"],
  rider_name: ["ridername", "rider", "member", "membername", "passenger", "passengername", "client", "clientname", "patient", "patientname"],
  broker: ["broker", "payer", "source", "program", "payor"],
  trip_type: ["triptype", "mobility", "los", "levelofservice", "space", "servicetype", "mode", "spacetype"],
  assigned_driver_name: ["assigneddrivername", "driver", "drivername", "assigneddriver"],
  price: ["price", "fare", "amount", "cost", "rate", "charge"],
};

function autoMap(fields, columns) {
  const used = new Set();
  const out = {};
  fields.forEach((f) => {
    const nf = norm(f);
    const aliases = ALIASES[f] || [];
    const hit = columns.find((c) => {
      if (used.has(c)) return false;
      const nc = norm(c);
      return nc === nf || aliases.includes(nc) || (nf.length > 3 && nc.includes(nf)) || aliases.some((a) => a.length > 3 && nc === a);
    });
    if (hit) { out[f] = hit; used.add(hit); } else { out[f] = SKIP; }
  });
  return out;
}

export default function ImportPage() {
  const [parsed, setParsed] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);
  const [importDays, setImportDays] = useState([]);
  const [dayCheck, setDayCheck] = useState(null);
  const [skipDupes, setSkipDupes] = useState(true);
  const [guideHidden, setGuideHidden] = useState(() => localStorage.getItem(GUIDE_KEY) === "1");
  const hideGuide = () => { localStorage.setItem(GUIDE_KEY, "1"); setGuideHidden(true); };
  const showGuide = () => { localStorage.removeItem(GUIDE_KEY); setGuideHidden(false); };
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadHistory = useCallback(() => {
    fleetApi.get("/imports/history").then((r) => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const [addrCheck, setAddrCheck] = useState(null);
  useEffect(() => {
    api.get("/addresses/flags").then((r) => { if (r.data.flags?.length) setAddrCheck({ flags: r.data.flags }); }).catch(() => {});
  }, []);
  const runAddressCheck = async (tripIds) => {
    if (!tripIds?.length) return;
    setAddrCheck({ loading: true });
    try {
      const { data } = await api.post("/addresses/validate-import", { trip_ids: tripIds });
      setAddrCheck(data);
      const bad = data.flags?.length || 0;
      if (bad) toast.warning(`${bad} address${bad === 1 ? "" : "es"} need${bad === 1 ? "s" : ""} review.`);
    } catch (e) {
      setAddrCheck({ error: formatApiError(e.response?.data?.detail) || "Address check failed." });
    }
  };
  const onFlagResolved = (id) => setAddrCheck((s) => ({ ...s, flags: (s?.flags || []).filter((f) => f.id !== id) }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setResult(null); setPreview(null); setParsed(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await fleetApi.post("/imports/parse-file", fd);
      setParsed(data);
      if (data.profile && Array.isArray(data.trips)) {
        setPreview({ mart: true });
        setDayCheck(null); setSkipDupes(true);
        checkImportDays(data.trips).then(setDayCheck).catch(() => setDayCheck([]));
        return;
      }
      const pv = await fleetApi.post("/imports/preview", { csv_text: data.csv_text, mapping: {} });
      const cols = pv.data.columns?.length ? pv.data.columns : (data.columns || []);
      const fields = pv.data.fields?.length ? pv.data.fields : TARGET_FIELDS;
      setPreview({ ...pv.data, columns: cols, fields });
      setMapping(autoMap(fields, cols));
    } catch (err) {
      toast.error(fleetError(err, "Couldn't read that file."));
    } finally { setUploading(false); e.target.value = ""; }
  };

  const commit = async () => {
    const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v && v !== SKIP));
    const isMart = !!preview?.mart;
    if (!isMart && Object.keys(clean).length === 0) { toast.error("Map at least one column before importing."); return; }
    setCommitting(true);
    try {
      const dupeIds = isMart && skipDupes ? (dayCheck || []).flatMap((d) => d.dupes) : [];
      const pinned = !isMart && clean.scheduled_pickup ? pinPickupTimes(parsed.csv_text, clean.scheduled_pickup) : dropRows(parsed.csv_text, dupeIds);
      const { csvText, mapping: sendMap } = isMart ? { csvText: pinned, mapping: {} } : mergeCodes(pinned, clean);
      const { data } = await fleetApi.post("/imports/commit", { csv_text: csvText, mapping: sendMap, filename: parsed.filename });
      runAddressCheck(data.trip_ids);
      setImportDays(isMart ? [...new Set((parsed.trips || []).map((t) => dayKey(t.scheduled_pickup)).filter(Boolean))].sort() : []);
      const n = data.imported ?? data.inserted ?? data.count ?? 0;
      toast.success(`Imported ${n} new trip${n === 1 ? "" : "s"}${data.updated ? `, updated ${data.updated}` : ""}${dupeIds.length ? ` · skipped ${dupeIds.length} already on the board` : ""}.`);
      setResult({ ...data, skipped_dupes: dupeIds.length });
      setParsed(null); setPreview(null); setMapping({}); setDayCheck(null);
      loadHistory();
    } catch (err) {
      toast.error(fleetError(err, "Import failed."));
    } finally { setCommitting(false); }
  };

  const reset = () => { setParsed(null); setPreview(null); setMapping({}); setResult(null); setImportDays([]); };
  const resultDays = useMemo(() => [...new Set([...importDays, ...((result?.days || []).map((d) => d.date))])].filter(Boolean).sort(), [importDays, result]);

  const columns = preview?.columns || [];
  const fields = preview?.fields || [];
  const sample0 = (preview?.sample || [])[0] || null;
  const noColumns = !!preview && !preview.mart && columns.length === 0;
  const mappedCount = Object.values(mapping).filter((v) => v && v !== SKIP).length;

  return (
    <div data-testid="import-page">
      <PageHeader title="Import Trips" subtitle="Bulk-upload a trip manifest (CSV or XLSX). MART files auto-detected." />

      {!preview && !result && !guideHidden && <ImportGuide onDismiss={hideGuide} />}

      {!preview && (
        <Panel testid="import-dropzone">
          <div className="flex flex-col items-center justify-center gap-4 px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400"><FileSpreadsheet className="h-7 w-7" /></span>
            <div>
              <p className="font-semibold text-white">Upload a trip manifest</p>
              <p className="mt-1 text-sm text-slate-500">CSV or XLSX with a header row. We'll detect the columns and let you map them.</p>
            </div>
            <label className="cursor-pointer">
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="import-file-input" disabled={uploading} />
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choose file
              </span>
            </label>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
              <button onClick={downloadSampleMart} className="text-blue-300 hover:underline" data-testid="import-download-sample-link">Download a sample MART file</button>
              {guideHidden && <button onClick={showGuide} className="text-slate-400 hover:text-slate-200 hover:underline" data-testid="import-show-guide">How importing works</button>}
            </div>
          </div>
        </Panel>
      )}

      {noColumns && (
        <Panel testid="import-no-columns">
          <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-400" />
            <p className="font-semibold text-white">We couldn't detect any columns in "{parsed?.filename}".</p>
            <p className="max-w-md text-sm text-slate-500">Make sure the file has a header row. If it's an Excel file, try re-saving it as <span className="font-mono-data">CSV (comma-delimited)</span> and upload again.</p>
            <Button onClick={reset} className="mt-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="import-no-columns-retry">Try another file</Button>
          </div>
        </Panel>
      )}

      {preview?.mart && (
        <>
          {dayCheck === null && <p className="mb-3 flex items-center gap-2 text-xs text-slate-400" data-testid="import-day-check-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking what's already on those days…</p>}
          {(dayCheck || []).some((d) => d.unknown) && <p className="mb-3 text-xs text-slate-400" data-testid="import-day-check-unknown">Couldn't check what's already on {dayCheck.filter((d) => d.unknown).map((d) => fmtDayKey(d.day)).join(", ")} — only the owner can look at past days.</p>}
          {(dayCheck || []).filter((d) => d.existing > 0).length > 0 && (
            <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100" data-testid="import-day-warning">
              <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Some of these days already have trips</p>
              <ul className="mt-2 space-y-1 text-xs">
                {dayCheck.filter((d) => d.existing > 0).map((d) => (
                  <li key={d.day} data-testid={`import-day-warning-${d.day}`}>
                    <span className="font-semibold">{fmtDayKey(d.day)}</span>: {d.existing} trip{d.existing === 1 ? "" : "s"} already there that aren't in this file{d.standing ? ` (${d.standing} standing orders added nightly)` : ""}.
                    {d.dupes.length > 0 && ` ${d.dupes.length} row${d.dupes.length === 1 ? " in this file repeats" : "s in this file repeat"} one of them (same rider, same time).`}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-amber-200/80">Importing keeps both. Existing trips stay as they are.</p>
              {dayCheck.some((d) => d.dupes.length) && (
                <label className="mt-2 flex items-center gap-2 text-xs font-semibold">
                  <input type="checkbox" checked={skipDupes} onChange={(e) => setSkipDupes(e.target.checked)} className="h-4 w-4 accent-amber-500" data-testid="import-skip-dupes" />
                  Skip {(() => { const n = dayCheck.reduce((s, d) => s + d.dupes.length, 0); return n === 1 ? "the 1 repeated row" : `the ${n} repeated rows`; })()} so nothing is doubled
                </label>
              )}
            </div>
          )}
          <MartPreview data={parsed} filename={parsed?.filename} committing={committing || dayCheck === null} onCommit={commit} onReset={reset} skip={skipDupes ? (dayCheck || []).reduce((s, d) => s + d.dupes.length, 0) : 0} />
        </>
      )}

      {preview && !preview.mart && !noColumns && (
        <div className="space-y-6">
          <Panel title={`Map columns · ${mappedCount} mapped`} testid="import-mapping"
            action={<button onClick={reset} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200" data-testid="import-reset"><X className="h-3.5 w-3.5" /> Start over</button>}>
            <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
              {fields.map((f) => (
                <div key={f}>
                  <label className="mb-1 block font-mono-data text-[11px] uppercase tracking-wider text-slate-400">{f.replace(/_/g, " ")}</label>
                  <select value={mapping[f] || SKIP} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}
                    className={darkField} data-testid={`import-map-${f}`}>
                    <option value={SKIP}>— skip —</option>
                    {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {mapping[f] && mapping[f] !== SKIP && sample0 && (
                    <div className="mt-1 truncate font-mono-data text-[11px] text-slate-500" data-testid={`import-hint-${f}`} title={String(sample0[mapping[f]] ?? "")}>
                      e.g. {String(sample0[mapping[f]] ?? "").trim() || "—"}
                      {f === "scheduled_pickup" && localToIso(sample0[mapping[f]]) && (
                        <span className="text-emerald-400" data-testid="import-tz-hint"> → {fmtZoned(localToIso(sample0[mapping[f]]))}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          <Panel title={`Preview (${preview.total_preview_rows ?? (preview.sample || []).length} rows)`} testid="import-preview">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                  {columns.map((c) => <th key={c} className="px-4 py-2.5">{c}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-slate-800">
                  {(preview.sample || []).length === 0 && (
                    <tr><td colSpan={Math.max(1, columns.length)} className="px-4 py-6 text-center text-slate-500" data-testid="import-no-rows">No data rows detected below the header.</td></tr>
                  )}
                  {(preview.sample || []).map((row, i) => (
                    <tr key={i} data-testid={`import-preview-row-${i}`}>
                      {columns.map((c) => <td key={c} className="px-4 py-2.5 text-slate-300">{String(row[c] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-slate-800 px-5 py-3">
              <span className="text-xs text-slate-500">Tip: map at least <span className="font-mono-data">rider name</span>, <span className="font-mono-data">pickup</span> and <span className="font-mono-data">dropoff</span>. Map <span className="font-mono-data">external trip id</span> to avoid duplicates.
                {!preview?.mart && codeColumns(preview?.columns, mapping).length > 0 && (
                  <span className="mt-1 block text-blue-300" data-testid="import-code-columns">Codes from {codeColumns(preview.columns, mapping).map((c) => `“${c}”`).join(", ")} will be added to each trip's type so dispatch can read them.</span>
                )}
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={reset} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="import-cancel">Cancel</Button>
                <Button onClick={commit} disabled={committing || mappedCount === 0} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="import-commit">
                  {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Import trips
                </Button>
              </div>
            </div>
          </Panel>
        </div>
      )}

      <AddressCheckPanel state={addrCheck} onResolved={onFlagResolved} />

      {result && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm text-emerald-200" data-testid="import-result">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
          <div className="flex-1">
            <p className="font-semibold">Import complete.</p>
            <p className="mt-1 font-mono-data text-emerald-300/90">
              {(result.imported ?? result.inserted ?? result.count ?? 0)} added · {result.updated ?? 0} updated{result.duplicates && result.duplicates !== result.updated ? ` · ${result.duplicates} duplicates` : ""}{result.skipped_dupes ? ` · ${result.skipped_dupes} skipped (already on the board)` : ""} · {result.rejected ?? 0} rejected
            </p>
            {resultDays.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="import-day-links">
                {resultDays.slice(0, 5).map((d) => (
                  <Button key={d} size="sm" onClick={() => navigate(`/portal/dispatch?date=${d}`)} data-testid={`import-view-day-${d}`}
                    className="gap-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-500">
                    View {fmtDayKey(d)} trips in Dispatch →
                  </Button>
                ))}
                {user?.role === "owner" && resultDays.slice(0, 5).map((d) => (
                  <span key={`ai-${d}`} className="contents">
                    <Button size="sm" onClick={() => { sessionStorage.setItem("rs_ai_ids", JSON.stringify(result.trip_ids || [])); navigate(`/portal/dispatch?date=${d}&ai=file`); }} data-testid={`import-ai-file-${d}`}
                      className="gap-1.5 rounded-full bg-blue-700 text-white hover:bg-blue-800">
                      <Sparkles className="h-3.5 w-3.5" /> AI assign this file's {fmtDayKey(d)} trips
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/portal/dispatch?date=${d}&ai=1`)} data-testid={`import-ai-assign-${d}`}
                      className="gap-1.5 rounded-full border-blue-500/50 bg-transparent text-blue-200 hover:bg-blue-500/10 hover:text-white">
                      AI assign every unassigned {fmtDayKey(d)} trip
                    </Button>
                  </span>
                ))}
                {resultDays.length > 5 && (
                  <span className="self-center text-xs text-slate-400" data-testid="import-more-days">+{resultDays.length - 5} more days — use the date chip in Dispatch</span>
                )}
              </div>
            )}
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-amber-300" data-testid="import-errors">
                {result.errors.slice(0, 5).map((er, i) => <li key={i}>{typeof er === "string" ? er : (er.msg || JSON.stringify(er))}</li>)}
              </ul>
            )}
            <ImportCodeSummary tripIds={result.trip_ids} days={resultDays} />
            {["owner", "dispatcher"].includes(user?.role) && (
              <ImportShiftWarning tripIds={result.trip_ids} days={resultDays} />
            )}
          </div>
        </div>
      )}

      <div className="mt-8">
        <Panel title="Recent Imports" testid="import-history">
          {history.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-slate-500">No imports yet.</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {history.map((h, i) => (
                <div key={h.id || i} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`import-history-${i}`}>
                  <span className="font-semibold text-white">{h.filename || "manifest"}</span>
                  <span className="font-mono-data text-xs text-slate-500">{h.created_at ? fmtDateTime(h.created_at) : ""} · {h.imported ?? h.row_count ?? h.count ?? 0} added{h.updated ? ` · ${h.updated} updated` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
